import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getMessaging, getToken, onMessage, deleteToken } from 'firebase/messaging';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);

let messaging = null;
try {
  messaging = getMessaging(app);
} catch (e) {
  console.log('FCM not supported in this browser');
}
export { messaging };

// Stable per-device ID for namespacing FCM tokens (one token per browser/install)
export function getDeviceId() {
  let id = localStorage.getItem('support_device_id');
  if (!id) {
    id = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem('support_device_id', id);
  }
  return id;
}

// Request notification permission + get FCM token.
// Handles iOS PWA recovery: when PWA is closed, ITP wipes IndexedDB and
// the push subscription becomes stale. deleteToken + retry recovers it.
export async function requestNotificationPermission() {
  if (!messaging) return null;
  if (!('serviceWorker' in navigator)) return null;
  if (!process.env.REACT_APP_FIREBASE_VAPID_KEY) {
    console.warn('REACT_APP_FIREBASE_VAPID_KEY missing — push will not work');
    return null;
  }
  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return null;
    const swReg = await navigator.serviceWorker.ready;
    let token = await getToken(messaging, {
      vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
      serviceWorkerRegistration: swReg,
    });
    // iOS PWA recovery: stale subscription returns null — wipe and retry.
    if (!token) {
      await deleteToken(messaging).catch(() => {});
      token = await getToken(messaging, {
        vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
        serviceWorkerRegistration: swReg,
      });
    }
    return token;
  } catch (err) {
    console.error('Notification permission error:', err);
    return null;
  }
}

export function onForegroundMessage(callback) {
  if (!messaging) return () => {};
  return onMessage(messaging, callback);
}
